import React, { createContext, useContext, ReactNode, useState, useCallback, useRef } from "react";
import { useCameraScanner, ScanResult, CameraDevice } from "hooks/useCameraScanner.ts";

export interface CameraContextValue {
  // State
  devices: CameraDevice[];
  selectedDeviceId: string;
  scanning: boolean;
  error: string;
  isInitialized: boolean;
  isInitializing: boolean;
  permissionState: 'unknown' | 'granted' | 'denied' | 'prompt';
  needsPermission: boolean;
  
  // Refs
  videoRef: React.RefObject<HTMLVideoElement>;
  
  // Actions
  startScanning: () => void;
  stopScanning: () => void;
  changeDevice: (deviceId: string) => void;
  retryInitialization: () => void;
  requestPermissionAndInitialize: () => void;
  handleVideoElementChange: (videoElement: HTMLVideoElement) => void;
  pauseProcessing: () => void;
  resumeProcessing: () => void;

  // Event emitter methods
  addScanListener: (listener: (result: ScanResult) => void) => void;
  removeScanListener: (listener: (result: ScanResult) => void) => void;
  addErrorListener: (listener: (error: string) => void) => void;
  removeErrorListener: (listener: (error: string) => void) => void;
}

const CameraContext = createContext<CameraContextValue | null>(null);

export interface CameraProviderProps {
  children: ReactNode;
  autoStart?: boolean;
}

export const CameraProvider: React.FC<CameraProviderProps> = ({
  children,
  autoStart = false,
}) => {
  const [processingPaused, setProcessingPaused] = useState(false);
  const processingPausedRef = useRef(false);
  const scanListenersRef = useRef<Set<(result: ScanResult) => void>>(new Set());
  const errorListenersRef = useRef<Set<(error: string) => void>>(new Set());

  const addScanListener = useCallback((listener: (result: ScanResult) => void) => {
    scanListenersRef.current.add(listener);
  }, []);

  const removeScanListener = useCallback((listener: (result: ScanResult) => void) => {
    scanListenersRef.current.delete(listener);
  }, []);

  const addErrorListener = useCallback((listener: (error: string) => void) => {
    errorListenersRef.current.add(listener);
  }, []);

  const removeErrorListener = useCallback((listener: (error: string) => void) => {
    errorListenersRef.current.delete(listener);
  }, []);

  const handleScan = useCallback((result: ScanResult) => {
    if (processingPausedRef.current) return;
    // Use refs to access current listeners without causing callback recreation
    scanListenersRef.current.forEach((listener: (result: ScanResult) => void) => listener(result));
  }, []);

  const handleError = useCallback((error: string) => {
    if (processingPausedRef.current) return;
    // Use refs to access current listeners without causing callback recreation
    errorListenersRef.current.forEach((listener: (error: string) => void) => listener(error));
  }, []);

  const cameraState = useCameraScanner({
    onScan: handleScan,
    onError: handleError,
    autoStart: autoStart && !processingPaused,
    enableDeviceSelection: true,
    persistDeviceSelection: true,
  });

  const pauseProcessing = useCallback(() => {
    processingPausedRef.current = true;
    setProcessingPaused(true);
  }, []);

  const resumeProcessing = useCallback(() => {
    processingPausedRef.current = false;
    setProcessingPaused(false);
  }, []);

  return (
    <CameraContext.Provider value={{
      ...cameraState,
      pauseProcessing,
      resumeProcessing,
      addScanListener,
      removeScanListener,
      addErrorListener,
      removeErrorListener,
    }}>
      {children}
    </CameraContext.Provider>
  );
};

export const useCameraContext = (): CameraContextValue => {
  const context = useContext(CameraContext);
  if (!context) {
    throw new Error("useCameraContext must be used within a CameraProvider");
  }
  return context;
}; 
